<template>
  <Modal
    v-model="modalVisible"
    :cancelText="(performMFAAction && numEligibleSystems) ? 'cancel' : ''"
    :handleMainAction="handleMainAction"
    headerText="Enable Multi-factor Authentication"
    :mainActionText="(performMFAAction && numEligibleSystems) ? 'enable' : 'ok'"
  >
    <div>
      <p v-if="!item">
        {{ numEligibleSystems }} of {{ selectedItems.length }} selected devices
        are eligible for MFA enablement.
      </p>
      <p v-if="ineligibleLinuxSystems.length">
        <span>
          Linux devices with SSH Password Login allowed and Public Key Authentication enabled
          ({{ pluralizedSystem }}) are
          <strong>not eligible</strong> for MFA enablement.
        </span>
      </p>
      <p>
        When MFA is enabled on a device, users who have set up TOTP or Push MFA
        will be prompted to authenticate their identity on their device upon login.
      </p>
    </div>
  </Modal>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import _ from 'lodash';
import mfaEnrollmentMixin from 'components/mixins/mfaEnrollmentMixin';
import mfaModalMixin from 'components/Modal/mfaModals/mfaModalMixin';
import pluralize from 'pluralize';

const { error, success } = Constants.default;

export default {
  name: 'SystemEnableMFAModal',

  mixins: [
    mfaEnrollmentMixin,
    mfaModalMixin,
  ],

  props: {
    /**
     * If this component is not rendered with a v-model (e.g. backbone),
     * you may want to use a custom function to update the `visible` prop passed
     */
    customUpdateVisibility: {
      type: Function,
      default: undefined,
    },
    item: {
      type: Object,
      default: undefined,
    },
    performMFAAction: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    ...mapGetters('SystemCollection', {
      ineligibleSystems: 'getSystemsIneligibleForMFAEnablement',
      selectedItems: 'getSelectedItems',
    }),

    alreadyEnabledSystems() { return this.ineligibleSystems.alreadyEnabledSystems; },

    ineligibleLinuxSystems() {
      return this.ineligibleSystems.ineligibleLinuxSystems;
    },

    modalVisible: {
      get() { return this.visible; },
      set(v) { this.updateModalVisibility(v); },
    },

    numEligibleSystems() {
      return this.selectedItems.length - this.alreadyEnabledSystems.length
        - this.ineligibleLinuxSystems.length;
    },

    pluralizedSystem() {
      return pluralize('system', this.ineligibleLinuxSystems.length, true);
    },
  },

  methods: {
    ...mapActions('SystemCollection', [
      'unselectItems',
      'updateItems',
    ]),

    /**
     * Enables MFA for systems that are selected
     */
    enableMFAForSystems() {
      const numSystems = this.item ? 1 : this.selectedItems.length;
      this.updateItems({
        ids: this.item ? [this.item.id] : this.selectedItems,
        attributes: { allowMultiFactorAuthentication: true },
      })
        .then((response) => {
          let notifyType;
          let notifyMessage;
          if (_.get(response, 'numSuccess')) {
            notifyType = success;
            notifyMessage = (numSystems === 1)
              ? '1 system was successfully updated.'
              : `${response.numSuccess} of ${numSystems} systems were successfully updated.`;
            this.unselectItems({ ids: this.selectedItems });
          } else {
            notifyType = error;
            notifyMessage = `Error updating ${pluralize('system', numSystems)}.`;
          }
          notify(notifyType, notifyMessage);
        });
    },

    /**
     * Executes when the user clicks the main action button in the modal.
     * Enables MFA for the selected systems. If there are systems that cannot have MFA
     * enabled (already active, Linux w/o SSH password auth), they will be unselected.
     */
    handleMainAction() {
      if (!this.numEligibleSystems || !this.performMFAAction) {
        this.updateModalVisibility(false);
        return;
      }

      try {
        this.updateModalVisibility(false);
        if (this.numEligibleSystems < this.selectedItems.length) {
          const allIneligibleSystems = this.alreadyEnabledSystems
            .concat(this.ineligibleLinuxSystems);
          this.unselectItems({ ids: allIneligibleSystems.map(item => item.id) });
        }
        this.enableMFAForSystems();
      } catch (err) {
        notify(error, err.message);
      }
    },

    updateModalVisibility(v) {
      if (this.customUpdateVisibility) {
        this.customUpdateVisibility(v);
        return;
      }
      this.$emit('updateVisibility', v);
    },
  },
};
</script>
